import {
  getPaymentDepositDateTime,
  isApprovedPaymentForDepositLedger,
} from "../paymentDepositLedgerUtils";
import { calculatePaymentSettlement } from "./paymentDepositSettlementUtils";
import {
  PaymentDepositCalendarCell,
  PaymentDepositCalendarDay,
  PaymentDepositCalendarItem,
  PaymentDepositCalendarRequest,
} from "./paymentDepositTypes";

const isDateKey = (value = "") => /^\d{4}-\d{2}-\d{2}$/.test(value);

const createDay = (date: string): PaymentDepositCalendarDay => ({
  date,
  transactions: [],
  expectedDeposits: [],
  actualDeposits: [],
  transactionAmount: 0,
  expectedDepositAmount: 0,
});

const getOrCreateDay = (
  days: Map<string, PaymentDepositCalendarDay>,
  date: string
) => {
  const current = days.get(date);
  if (current) return current;
  const day = createDay(date);
  days.set(date, day);
  return day;
};

export const buildPaymentDepositCalendarDayMap = (
  requests: PaymentDepositCalendarRequest[] = []
) => {
  const days = new Map<string, PaymentDepositCalendarDay>();
  requests.forEach((request) => {
    if (!isApprovedPaymentForDepositLedger(request)) return;
    const approved = getPaymentDepositDateTime(request);
    if (!isDateKey(approved.date)) return;
    const settlement = calculatePaymentSettlement(request, approved.date);
    const item: PaymentDepositCalendarItem = { request, settlement };
    const transactionDay = getOrCreateDay(days, approved.date);
    transactionDay.transactions.push(item);
    transactionDay.transactionAmount += settlement.settlementAmount;

  });
  return days;
};

export const getKoreaDateKey = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

export const shiftPaymentDepositMonth = (
  monthKey: string,
  monthOffset: number
) => {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (!match) return getKoreaDateKey().slice(0, 7);
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1 + monthOffset, 1)
  );
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
    2,
    "0"
  )}`;
};

export const buildPaymentDepositCalendarCells = ({
  monthKey,
  dateFrom = "",
  dateTo = "",
  today = getKoreaDateKey(),
  days = new Map<string, PaymentDepositCalendarDay>(),
}: {
  monthKey: string;
  dateFrom?: string;
  dateTo?: string;
  today?: string;
  days?: Map<string, PaymentDepositCalendarDay>;
}): PaymentDepositCalendarCell[] => {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (!match) return [];
  const year = Number(match[1]);
  const month = Number(match[2]);
  const first = new Date(Date.UTC(year, month - 1, 1));
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const leading = first.getUTCDay();
  const cellCount = Math.ceil((leading + lastDay) / 7) * 7;
  return Array.from({ length: cellCount }, (_, index) => {
    const dayOfMonth = index - leading + 1;
    if (dayOfMonth < 1 || dayOfMonth > lastDay) {
      return {
        date: "",
        dayOfMonth: 0,
        inMonth: false,
        inRange: false,
        isToday: false,
      };
    }
    const date = `${monthKey}-${String(dayOfMonth).padStart(2, "0")}`;
    return {
      date,
      dayOfMonth,
      inMonth: true,
      inRange:
        (!dateFrom || date >= dateFrom) && (!dateTo || date <= dateTo),
      isToday: date === today,
      summary: days.get(date),
    };
  });
};

export const formatPaymentDepositMonthTitle = (monthKey: string) => {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey);
  return match ? `${Number(match[1])}년 ${Number(match[2])}월` : monthKey;
};
